/**
 * VAGUE 35 — Share link generator for telepros.
 *
 * One-tap "Copier le lien" that bundles the telepro code as `?ref=` so the
 * prospect lands on /inscription with the code already filled in and a
 * welcome banner — no more "which code was it again?" drop-offs.
 */
import { useState } from "react";
import { Link as LinkIcon, Copy, CheckCircle, Share, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TeleproShareLinkCard({ teleproCode }) {
  const [copied, setCopied] = useState(false);
  if (!teleproCode) return null;

  const base = typeof window !== "undefined" ? window.location.origin : "https://www.moncommerce68.fr";
  const url = `${base}/inscription?ref=${teleproCode}`;
  const waText = encodeURIComponent(
    `Bonjour ! Suite à notre échange, voici le lien pour créer votre compte MonCommerce68 en 1 minute (14 jours d'essai gratuit, sans engagement) :\n\n${url}\n\nMon code conseiller est déjà pré-rempli. Besoin d'aide ? Rappelez-moi.`
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Lien copié ! Colle-le dans WhatsApp / SMS.");
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {
      toast.error("Copie impossible — long-tap pour sélectionner et copier");
    }
  };

  return (
    <div className="mb-4 border border-primary/30 bg-primary/5 rounded-sm p-3" data-testid="telepro-share-link-card">
      <div className="flex items-center gap-2 mb-1">
        <LinkIcon size={14} weight="duotone" className="text-primary"/>
        <h3 className="font-display text-sm font-medium">Ton lien d'inscription à partager</h3>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">
        Envoie <b>ce lien</b> par SMS/WhatsApp au prospect intéressé. Ton code
        est déjà pré-rempli — <b className="text-primary">plus d'excuse pour les 20+ pertes</b> !
      </p>
      <div className="flex items-center gap-1 bg-card border border-border rounded-sm px-2 py-1.5 mb-2 overflow-hidden">
        <code className="flex-1 text-[11px] font-mono truncate text-muted-foreground">{url}</code>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={copy}
          className="flex-1 inline-flex items-center justify-center gap-1 bg-primary text-primary-foreground hover:bg-[#0f3d29] px-3 py-2 rounded-sm text-xs font-semibold"
          data-testid="share-link-copy"
        >
          {copied ? <><CheckCircle size={12} weight="fill"/> Copié !</> : <><Copy size={12} weight="bold"/> Copier</>}
        </button>
        <a
          href={`https://wa.me/?text=${waText}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-1 bg-[#25D366] text-white hover:bg-[#1da851] px-3 py-2 rounded-sm text-xs font-semibold"
          data-testid="share-link-whatsapp"
        >
          <WhatsappLogo size={14} weight="fill"/> WhatsApp
        </a>
        {typeof navigator !== "undefined" && navigator.share && (
          <button
            onClick={() => navigator.share({ title: "Rejoignez MonCommerce68", text: "Créez votre compte gratuit :", url }).catch(() => {})}
            className="inline-flex items-center justify-center gap-1 bg-card border border-border hover:border-primary px-3 py-2 rounded-sm text-xs font-semibold"
            aria-label="Partager"
            data-testid="share-link-native"
          >
            <Share size={12} weight="bold"/>
          </button>
        )}
      </div>
    </div>
  );
}
